import {
  Alert,
  AlertBody,
  AlertIcon,
} from '@lifeomic/chroma-react/components/Alert';
import { BookOpen, Info } from '@lifeomic/chromicons';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { defineMessages, useIntl } from 'react-intl';
import { LinearProgress } from '@lifeomic/chroma-react/components/LinearProgress';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { Markdown } from '../pages/Files/FileDetailsModal/Markdown';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { useABAC } from '@lifeomic/phc-web-toolkit/src/hooks/useABAC';
import { useActiveProjectId } from '@lifeomic/phc-web-toolkit/src/hooks/useActiveProjectId';
import { useQuery } from '@tanstack/react-query';
import * as React from 'react';
import clsx from 'clsx';
import FileService from '@lifeomic/phc-web-toolkit/src/services/FileService';
import useServiceAuth from '@lifeomic/phc-web-toolkit/src/hooks/useServiceAuth';

const fileService = new FileService();

const messages = defineMessages({
  readMe: {
    id: 'phc-ui.common.projectReadMe.readMe',
    defaultMessage: 'README.md',
  },
  noReadMeFound: {
    id: 'phc-ui.common.projectReadMe.noReadMeFound',
    defaultMessage: 'A "README.md" file was not found for this project.',
  },
  noReadMeFoundHelp: {
    id: 'phc-ui.common.projectReadMe.noReadMeFoundHelp',
    defaultMessage:
      'Consider adding one to share information about the project.',
  },
});

const useStyles = makeStyles(theme => ({
  '@keyframes fade-in': {
    '0%': {
      opacity: 0,
    },
    '100%': {
      opacity: 1,
    },
  },
  root: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.pxToRem(4),
  },
  header: {
    backgroundColor: theme.palette.primary.main,
    borderTopRightRadius: theme.pxToRem(3),
    borderTopLeftRadius: theme.pxToRem(3),
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1),
  },
  headerIcon: {
    color: theme.palette.common.white,
    marginRight: theme.spacing(1),
  },
  content: {
    backgroundColor: theme.palette.background.paper,
    position: 'relative',
    borderBottomLeftRadius: theme.pxToRem(3),
    borderBottomRightRadius: theme.pxToRem(3),
  },
  progressBar: {
    position: 'absolute',
    top: '-0.25rem',
    width: '100%',
  },
  fadeInAnimation: {
    animation: '$fade-in 1s',
  },
  pxy: {
    padding: theme.spacing(1),
  },
  fullWidth: {
    width: '100%',
  },
  m: {
    margin: theme.spacing(1),
  },
}));

interface ProjectReadMeProps {
  className?: string;
}

export const ProjectReadMe: React.FC<ProjectReadMeProps> = ({ className }) => {
  const classes = useStyles({});
  const { formatMessage } = useIntl();

  const auth = useServiceAuth();
  const activeProjectId = useActiveProjectId();

  const [canPerform, permissions] = useABAC();

  const { data, isLoading } = useQuery(
    ['project-read-me', activeProjectId],
    async () => {
      if (!canPerform(permissions.downloadFile)) {
        return null;
      }

      if (!activeProjectId) {
        return null;
      }

      // Query for a README file in the project files
      const { data: projectFilesResponse } =
        await fileService.fetchProjectFiles(auth)({
          name: 'README.md',
          projectId: activeProjectId,
        });

      if (projectFilesResponse?.items?.length === 0) {
        return null;
      }

      const [readmeFile] = projectFilesResponse.items;

      // Fetch the open link data for this particular README
      const { data: openLinkResponse } = await fileService.fetchOpenLink(auth)(
        readmeFile.id,
      );

      if (!openLinkResponse?.downloadUrl) {
        return null;
      }

      // Now we have a download URL, so we can actually fetch the file
      const actualFileResponse = await fetch(openLinkResponse.downloadUrl);
      const asText = await actualFileResponse.text();

      return asText;
    },
  );

  if (!canPerform(permissions.downloadFile)) {
    return null;
  }

  return (
    <Box className={clsx(classes.root, className)} direction="column">
      <Box className={classes.header} align="center">
        <BookOpen className={classes.headerIcon} height={14} width={14} />
        <Text color="inverse" size="caption">
          {formatMessage(messages.readMe)}
        </Text>
      </Box>
      <Box className={classes.content}>
        {isLoading && (
          <LinearProgress
            className={classes.progressBar}
            variant="indeterminate"
          />
        )}
        {data && (
          <Markdown
            className={clsx(
              classes.fadeInAnimation,
              classes.pxy,
              classes.fullWidth,
            )}
            source={data}
          />
        )}
        {!isLoading && !data && (
          <Alert
            className={clsx(classes.fadeInAnimation, classes.m)}
            statusType="info"
            fullWidth
          >
            <AlertIcon icon={Info} />
            <AlertBody>
              <Text weight="bold" size="subbody">
                {formatMessage(messages.noReadMeFound)}
              </Text>
              <Text size="caption">
                {formatMessage(messages.noReadMeFoundHelp)}
              </Text>
            </AlertBody>
          </Alert>
        )}
      </Box>
    </Box>
  );
};
